/*
 * Purpose: Provide auth access and password service methods via transport client.
 * Boundary: Service wrapper only; no UI logic; all network calls delegated to transport.
 * Invariants:
 * - Uses transport.withBase(base) per resource group.
 * - Backend field names preserved (even if inconsistent).
 * - ASCII only; no Unicode characters in comments or strings.
 */

// Imports
import transport from "./transport.js";

// Constants - base paths and scoped transport instances
const api_base = "/api";
const authaccess_base = api_base + "/auth/access";
const authaccess_http = transport.withBase(authaccess_base);

const password_base = api_base + "/auth/password";
const password_http = transport.withBase(password_base);

// Function requestLogin:
// Send request for login using ID and password
async function requestLogin(req) {
    return authaccess_http.postJSON("/requestLogin", {
        "IdNumber": req.idNumber, "CellphoneNumber": req.cellphoneNumber, "CountryCode": req.countryCode, "Password": req.password
    });
}

// Function requestRegistration:
// Request for registration
async function requestRegistration(req) {
    return authaccess_http.postJSON("/requestRegistration", {
        "FirstName": req.firstName, "LastName": req.lastName, "IdNumber": req.idNumber,
        "CellphoneNumber": req.cellphoneNumber, "CountryCode": req.countryCode, "Password": req.password,
        "legalGuardian": req.legalGuardian,
        "ReferralCode": req.referralCode,
        "TrackingConsentAccepted": req.trackingConsentAccepted
        });
}
// Function sendOneTimeCode:
// Send verification code to channel address (cell or email) with optional channel type
async function sendOneTimeCode(req) {
    const body = { "ChannelAddress": req.channelAddress, "IdNumber": req.idNumber };
    if (req.channelType != null) body.ChannelType = req.channelType;
    return authaccess_http.postJSON("/sendOneTimeCode", body);
}
// Function resendOneTimeCode:
// Resend verification code to channel address with optional channel type
async function resendOneTimeCode(req) {
    const body = { "ChannelAddress": req.channelAddress, "IdNumber": req.idNumber };
    if (req.channelType != null) body.ChannelType = req.channelType;
    return authaccess_http.postJSON("/resendOneTimeCode", body);
}
// Function validateOneTimeCode:
// Validate verification code for channel address with optional channel type
async function validateOneTimeCode(req) {
    const body = { "ChannelAddress": req.channelAddress, "OneTimeCode": req.oneTimeCode, "IdNumber": req.idNumber };
    if (req.channelType != null) body.ChannelType = req.channelType;
    return authaccess_http.postJSON("/validateOneTimeCode", body);
}
// Function generatePassword:
// Request server-side password generation
async function generatePassword(req) {
    return password_http.postJSON("/generate", {
        "Type": req.type, "Length": req.length
        });
}
// Function generatePassword:
// Request server-side password generation
async function resetPassword(req) {
    return password_http.postJSON("/reset", {
        "CellphoneNumber": req.cellphoneNumber, "IdNumber": req.idNumber, "CountryCode": req.countryCode, "Password": req.password
        });
}
// Function sendProfileEmailVerification:
// Send email verification code for profile email create/change flows
async function sendProfileEmailVerification(req) {
    const body = { "ChannelAddress": req.channelAddress, "IdNumber": req.idNumber };
    if (req.channelType != null) body.ChannelType = req.channelType;
    return authaccess_http.postJSON("/sendProfileEmailVerification", body);
}
// Function validateProfileEmailVerification:
// Validate email verification code for profile email create/change flows
async function validateProfileEmailVerification(req) {
    const body = { "ChannelAddress": req.channelAddress, "OneTimeCode": req.oneTimeCode, "IdNumber": req.idNumber };
    if (req.channelType != null) body.ChannelType = req.channelType;
    return authaccess_http.postJSON("/validateProfileEmailVerification", body);
}
// Function checkConfirmedEmail:
// Silent lookup to check if user has a confirmed email on file
async function checkConfirmedEmail(req) {
    const encoded = btoa(req.idNumber);
    return password_http.request(`/checkConfirmedEmail?idNumber=${encodeURIComponent(encoded)}`);
}

// Function getRegistrationAgreementText:
// Fetch server-generated T&C text for registration form checkboxes
async function getRegistrationAgreementText() {
    return authaccess_http.request("/registrationAgreementText");
}

// Function logout:
// Call backend to delete pc_auth cookie; raw fetch because token may be expired
async function logout() {
    try {
        await fetch('/api/auth/access/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
        // Network error is acceptable -- cookie will be stale anyway, redirect will fix it
    }
}

// Export - expose all service functions for external use
export default {
    requestLogin,
    requestRegistration,
    sendOneTimeCode,
    resendOneTimeCode,
    validateOneTimeCode,
    sendProfileEmailVerification,
    validateProfileEmailVerification,
    generatePassword,
    resetPassword,
    checkConfirmedEmail,
    getRegistrationAgreementText,
    logout
};